import { useState } from 'react';
import { Plus, Trash2, Edit2, AlertCircle } from 'lucide-react';
import {
  getSuggestedExpenseSections,
  getSuggestedRevenueSections
} from '../../utils/forecastEngine';

interface ForecastLinesTabProps {
  forecastData: any;
}

export default function ForecastLinesTab({ forecastData }: ForecastLinesTabProps) {
  const { scenario, dimensions, lineItems, createLineItem, updateLineItem, deleteLineItem } =
    forecastData;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLine, setEditingLine] = useState<any | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'expense' | 'revenue'>('all');
  const [filterSection, setFilterSection] = useState<string>('all');

  // Form state
  const [formType, setFormType] = useState<'expense' | 'revenue'>('expense');
  const [formSection, setFormSection] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formRuleType, setFormRuleType] = useState<
    'fixed' | 'growth' | 'per_unit' | 'threshold'
  >('fixed');
  const [formBaseAmount, setFormBaseAmount] = useState('0');
  const [formGrowthRate, setFormGrowthRate] = useState('');
  const [formDimensionId, setFormDimensionId] = useState('');
  const [formAmountPerUnit, setFormAmountPerUnit] = useState('');
  const [formThresholdValue, setFormThresholdValue] = useState('');
  const [formThresholdAmount, setFormThresholdAmount] = useState('');
  const [formActiveFromYear, setFormActiveFromYear] = useState(1);
  const [formActiveToYear, setFormActiveToYear] = useState(scenario.years_count);
  const [formNotes, setFormNotes] = useState('');

  const resetForm = () => {
    setFormType('expense');
    setFormSection('');
    setFormLabel('');
    setFormRuleType('fixed');
    setFormBaseAmount('0');
    setFormGrowthRate('');
    setFormDimensionId('');
    setFormAmountPerUnit('');
    setFormThresholdValue('');
    setFormThresholdAmount('');
    setFormActiveFromYear(1);
    setFormActiveToYear(scenario.years_count);
    setFormNotes('');
  };

  const loadLineToForm = (line: any) => {
    setFormType(line.type);
    setFormSection(line.section);
    setFormLabel(line.label);
    setFormRuleType(line.rule_type);
    setFormBaseAmount(line.base_amount?.toString() || '0');
    setFormGrowthRate(line.growth_rate?.toString() || '');
    setFormDimensionId(line.dimension_id || '');
    setFormAmountPerUnit(line.amount_per_unit?.toString() || '');
    setFormThresholdValue(line.threshold_unit_value?.toString() || '');
    setFormThresholdAmount(line.threshold_amount?.toString() || '');
    setFormActiveFromYear(line.active_from_year);
    setFormActiveToYear(line.active_to_year);
    setFormNotes(line.notes || '');
  };

  const handleCreate = async () => {
    if (!formLabel.trim() || !formSection.trim()) {
      alert('Veuillez remplir le libellé et la section');
      return;
    }

    try {
      await createLineItem({
        scenario_id: scenario.id,
        type: formType,
        section: formSection.trim(),
        label: formLabel.trim(),
        rule_type: formRuleType,
        base_amount: parseFloat(formBaseAmount) || 0,
        growth_rate: formGrowthRate ? parseFloat(formGrowthRate) : null,
        dimension_id: formDimensionId || null,
        amount_per_unit: formAmountPerUnit ? parseFloat(formAmountPerUnit) : null,
        threshold_unit_value: formThresholdValue ? parseFloat(formThresholdValue) : null,
        threshold_amount: formThresholdAmount ? parseFloat(formThresholdAmount) : null,
        active_from_year: formActiveFromYear,
        active_to_year: formActiveToYear,
        notes: formNotes.trim() || null
      });

      resetForm();
      setShowCreateModal(false);
    } catch (error) {
      alert('Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    if (!editingLine || !formLabel.trim() || !formSection.trim()) {
      alert('Veuillez remplir le libellé et la section');
      return;
    }

    try {
      await updateLineItem(editingLine.id, {
        type: formType,
        section: formSection.trim(),
        label: formLabel.trim(),
        rule_type: formRuleType,
        base_amount: parseFloat(formBaseAmount) || 0,
        growth_rate: formGrowthRate ? parseFloat(formGrowthRate) : null,
        dimension_id: formDimensionId || null,
        amount_per_unit: formAmountPerUnit ? parseFloat(formAmountPerUnit) : null,
        threshold_unit_value: formThresholdValue ? parseFloat(formThresholdValue) : null,
        threshold_amount: formThresholdAmount ? parseFloat(formThresholdAmount) : null,
        active_from_year: formActiveFromYear,
        active_to_year: formActiveToYear,
        notes: formNotes.trim() || null
      });

      resetForm();
      setEditingLine(null);
    } catch (error) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette ligne ?')) return;

    try {
      await deleteLineItem(id);
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const filteredLines = lineItems.filter(line => {
    if (filterType !== 'all' && line.type !== filterType) return false;
    if (filterSection !== 'all' && line.section !== filterSection) return false;
    return true;
  });

  const allSections = Array.from(new Set(lineItems.map(l => l.section)));

  const getRuleTypeLabel = (ruleType: string) => {
    switch (ruleType) {
      case 'fixed':
        return 'Montant fixe';
      case 'growth':
        return 'Croissance';
      case 'per_unit':
        return 'Par unité';
      case 'threshold':
        return 'Seuil';
      default:
        return ruleType;
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Lignes budgétaires</h2>
          <p className="text-sm text-gray-600 mt-1">
            Définissez vos charges et produits prévisionnels
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={18} />
          Ajouter une ligne
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tous les types</option>
          <option value="revenue">Produits seulement</option>
          <option value="expense">Charges seulement</option>
        </select>

        <select
          value={filterSection}
          onChange={e => setFilterSection(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Toutes les sections</option>
          {allSections.map(section => (
            <option key={section} value={section}>
              {section}
            </option>
          ))}
        </select>
      </div>

      {filteredLines.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">Aucune ligne budgétaire créée</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Section</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Libellé</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Règle</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Période</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredLines.map(line => (
                <tr key={line.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        line.type === 'revenue'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {line.type === 'revenue' ? 'Produit' : 'Charge'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{line.section}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{line.label}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {getRuleTypeLabel(line.rule_type)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    A{line.active_from_year}-A{line.active_to_year}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          loadLineToForm(line);
                          setEditingLine(line);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="Modifier"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(line.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showCreateModal || editingLine) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full my-8">
            <h3 className="text-lg font-semibold mb-4">
              {editingLine ? 'Modifier la ligne' : 'Nouvelle ligne budgétaire'}
            </h3>

            <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={formType}
                    onChange={e => setFormType(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="revenue">Produit (recette)</option>
                    <option value="expense">Charge (dépense)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
                  <input
                    type="text"
                    list="sections-list"
                    value={formSection}
                    onChange={e => setFormSection(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Personnel"
                  />
                  <datalist id="sections-list">
                    {(formType === 'expense'
                      ? getSuggestedExpenseSections()
                      : getSuggestedRevenueSections()
                    ).map(section => (
                      <option key={section} value={section} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Libellé *</label>
                <input
                  type="text"
                  value={formLabel}
                  onChange={e => setFormLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Directeur, Location bâtiment..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type de calcul *
                </label>
                <select
                  value={formRuleType}
                  onChange={e => setFormRuleType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="fixed">Montant fixe (même montant chaque année)</option>
                  <option value="growth">Croissance (augmentation % par an)</option>
                  <option value="per_unit">Par unité (montant × dimension)</option>
                  <option value="threshold">
                    Seuil (montant si dimension atteint un seuil)
                  </option>
                </select>
              </div>

              {formRuleType === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant annuel *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formBaseAmount}
                    onChange={e => setFormBaseAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {formRuleType === 'growth' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Montant année 1 *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formBaseAmount}
                      onChange={e => setFormBaseAmount(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Croissance annuelle (%) *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formGrowthRate}
                      onChange={e => setFormGrowthRate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="5"
                    />
                  </div>
                </div>
              )}

              {formRuleType === 'per_unit' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimension *
                    </label>
                    <select
                      value={formDimensionId}
                      onChange={e => setFormDimensionId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner...</option>
                      {dimensions.map(dim => (
                        <option key={dim.id} value={dim.id}>
                          {dim.label} ({dim.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Montant par unité *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formAmountPerUnit}
                      onChange={e => setFormAmountPerUnit(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="8000"
                    />
                  </div>
                </div>
              )}

              {formRuleType === 'threshold' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimension *
                    </label>
                    <select
                      value={formDimensionId}
                      onChange={e => setFormDimensionId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Sélectionner...</option>
                      {dimensions.map(dim => (
                        <option key={dim.id} value={dim.id}>
                          {dim.label} ({dim.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Seuil (valeur dimension) *
                      </label>
                      <input
                        type="number"
                        step="1"
                        value={formThresholdValue}
                        onChange={e => setFormThresholdValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="16"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Montant si seuil atteint *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formThresholdAmount}
                        onChange={e => setFormThresholdAmount(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="35000"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Active à partir de l'année
                  </label>
                  <select
                    value={formActiveFromYear}
                    onChange={e => setFormActiveFromYear(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(y => (
                      <option key={y} value={y}>
                        Année {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Active jusqu'à l'année
                  </label>
                  <select
                    value={formActiveToYear}
                    onChange={e => setFormActiveToYear(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {Array.from({ length: scenario.years_count }, (_, i) => i + 1).map(y => (
                      <option key={y} value={y}>
                        Année {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optionnel)
                </label>
                <textarea
                  value={formNotes}
                  onChange={e => setFormNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Informations complémentaires..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingLine(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={editingLine ? handleUpdate : handleCreate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {editingLine ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
